/**
 * The dashboard's first-visit state.
 *
 * WHY THIS EXISTS. A client meeting on 2026-08-20 put a non-financial reader through the whole
 * product. He handled the fact-find; the dashboard stopped him dead — "he felt completely lost...
 * he eventually clicked on Next Steps". Reading the page as he saw it explains why: on a first
 * visit EIGHT of its eleven blocks are empty, five of them "No <module> strategies saved yet" in
 * the widest column, so the eye is pulled to the emptiest part of the screen — while the one thing
 * he should do sits in a narrow card on the right, below the fold on a laptop.
 *
 * So on a first visit the dashboard leads with what to do, and stops showing shelves that hold
 * nothing. Nothing is deleted: the folded sections are one click away, and the moment anything is
 * saved this state is gone and the full dashboard returns unchanged.
 *
 * WHAT COUNTS AS A FIRST VISIT is "nothing saved yet", not "has logged in before" — owner's call,
 * 2026-08-22. Someone who arrives, feels lost and leaves without modelling anything is exactly the
 * person who needs this on their next visit, and a once-only flag would have taken it away from
 * them.
 *
 * The voice is NAME-LED, matching the fact-find: the reader is often a son or daughter, so this
 * says "George's profile" rather than "your profile" unless they are the applicant themselves.
 */
import React from 'react';
import { ArrowRight, PencilLine, ChevronDown } from 'lucide-react';
import { GOAL_ICONS } from '../../components/icons/moduleIcons.ts';
import IconTile from '../../components/ui/IconTile.tsx';
import type { LucideIcon } from 'lucide-react';
import type { DashboardGoalKey } from './dashboardConfig.tsx';

export interface StartHereAction {
    /** The profile goal it answers; 'cashflow' has none and shows once a cashflow plan is saved. */
    goal: DashboardGoalKey | 'cashflow';
    page: string;
    icon: LucideIcon | React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
    /** Rendered after the possessive for whoever THIS goal belongs to, e.g. "Michael's" + " aged care costs". */
    title: (possessive: string) => string;
    blurb: string;
    /**
     * The fact-find field recording who this goal is for, where one exists.
     *
     * Residential care and home care are decided PER PERSON — one of a couple can be entering care
     * while both are on home care packages — so the profile asks, and this card must use the answer.
     * A village move, a downsize and a granny flat are household decisions with no such field, so
     * they carry both names for a couple.
     */
    forField?: 'aged_care_for' | 'home_care_for';
}

/**
 * One action per goal the client actually ticked, in the order they are offered in the profile.
 * Each blurb answers "what will I get if I click this", in the reader's terms rather than the
 * module's name.
 */
export const START_HERE_ACTIONS: StartHereAction[] = [
    {
        goal: 'aged_care',
        page: 'AgedCarePage',
        icon: GOAL_ICONS.aged_care,
        title: (p) => `Model ${p} aged care costs`,
        blurb: 'What a room costs, what would be paid each year, and how long the savings last.',
        forField: 'aged_care_for',
    },
    {
        goal: 'home_care',
        page: 'HomeCarePage',
        icon: GOAL_ICONS.home_care,
        title: (p) => `Model ${p} home care support`,
        blurb: 'What Support at Home would contribute, and what would be paid towards it.',
        forField: 'home_care_for',
    },
    {
        goal: 'retirement_village',
        page: 'RetirementVillagePage',
        icon: GOAL_ICONS.retirement_village,
        title: (p) => `Compare retirement village options`,
        blurb: 'Entry contribution, ongoing fees, and what comes back on leaving.',
    },
    {
        goal: 'downsize_home',
        page: 'DownsizingPage',
        icon: GOAL_ICONS.downsize_home,
        title: (p) => `Model downsizing ${p} home`,
        blurb: 'What selling frees up, and what it does to the Age Pension.',
    },
    {
        goal: 'granny_flat',
        page: 'GrannyFlatPage',
        icon: GOAL_ICONS.granny_flat,
        title: (p) => `Explore a granny flat arrangement`,
        blurb: 'What can be paid for the right to live there, and how Centrelink treats it.',
    },
];

/** Cashflow & Age Pension: no goal of its own, so a row only once a plan of it is saved (owner, 19 Oct 2026). */
export const CASHFLOW_START_HERE_ACTION: StartHereAction = {
    goal: 'cashflow',
    page: 'CashflowPensionPage',
    icon: GOAL_ICONS.cashflow,
    title: (p) => `Model ${p} cashflow and Age Pension`,
    blurb: 'Where the money stands today, how far it goes, and what could improve the Age Pension.',
};

/**
 * Where one living arrangement stands, for its row (owner, 23 Oct 2026). The tiles that replaced these rows
 * once a plan was chosen made Home too busy, with the saved plans already listed beneath; so every state is
 * the one-row banner again, and a row that has a saved plan carries two doors:
 *   - "Open your plan" (a plan is chosen: its plan page) or "Open last saved" (none chosen: the latest save),
 *     where changes to the existing model are made;
 *   - "Start": a fresh start, nothing carried over and step 3's questions asked again. The chosen plan stays
 *     chosen until another one is chosen.
 */
export interface StartHereRow {
    savedCount: number;
    /** The chosen plan's title, when one is chosen. */
    chosenTitle?: string | null;
    /** Why the chosen plan's figures are out of date (chosenPlanRefresh.ts, sahChosenStale.ts), or null. */
    outOfDate?: string | null;
    onOpen: () => void;
    onStartFresh: () => void;
    /** "Compare the homes" / "Compare the units", when more than one is modelled. */
    compare?: { label: string; onClick: () => void } | null;
}

/**
 * Whose goal is this, in possessive form.
 *
 * @param forWhom  the fact-find's own answer: 'client', 'partner', 'both', or absent for a
 *                 household-level goal (a village move, a downsize, a granny flat) which belongs to
 *                 everyone in the profile.
 * @param readerIsApplicant  true when the person reading is the applicant themselves, in which case
 *                 their own half of the phrase is "you" rather than their name.
 */
export function goalPossessive(
    forWhom: string | undefined,
    names: { client?: string; partner?: string },
    isCouple: boolean,
    readerIsApplicant: boolean,
): string {
    const clientPossessive = readerIsApplicant ? 'your' : names.client ? `${names.client}'s` : 'their';
    const partnerPossessive = names.partner ? `${names.partner}'s` : 'their';

    if (!isCouple) return clientPossessive;
    if (forWhom === 'client') return clientPossessive;
    if (forWhom === 'partner') return partnerPossessive;

    // 'both', or a household goal with no answer recorded. ONE possessive at the end — "Michael and
    // Tara's home care", not "Michael's and Tara's home care", which is the owner's own phrasing and
    // the way people actually say it. The reader's own half is "you", since nobody refers to
    // themselves by name.
    const first = readerIsApplicant ? 'you' : names.client;
    if (!first && !names.partner) return 'their';
    if (!names.partner) return clientPossessive;
    if (!first) return partnerPossessive;
    return `${first} and ${names.partner}'s`;
}

/** The heading, which must never call an unfinished profile complete (owner, 16 Oct 2026). */
export function startHereHeading(Possessive: string, completeness: { isComplete: boolean; isEmpty: boolean; percent: number } | null | undefined): string {
    if (!completeness || completeness.isComplete) return `${Possessive} profile is complete.`;
    // "Your" drops its capital mid-sentence; a name keeps it.
    if (completeness.isEmpty) return `Let's start with ${Possessive === 'Your' ? 'your' : Possessive} profile.`;
    return `${Possessive} profile is ${completeness.percent}% complete.`;
}

/** The profile step to open next: the first unfinished one (step 1 is "Who's completing"; the seven assessed steps follow it). */
export function nextProfileStep(completeness: { steps: Array<{ complete: boolean }>; isEmpty?: boolean } | null | undefined): number {
    // Nothing entered yet: from the beginning, "who is completing", not the first assessed step.
    if (completeness?.isEmpty) return 1;
    const i = (completeness?.steps ?? []).findIndex((st) => !st.complete);
    return i < 0 ? 1 : i + 2;
}

interface StartHereCardProps {
    goals: Record<string, unknown> | undefined;
    /** How far the profile has got (shared/src/factFind/profileCompleteness.ts). Absent reads as complete. */
    completeness?: { isComplete: boolean; isEmpty: boolean; percent: number; steps: Array<{ label: string; complete: boolean }> } | null;
    setClientProfileStep?: (step: number) => void;
    /**
     * The HOUSEHOLD possessive, capitalised, for the heading: "Michael & Tara's profile is complete."
     * The profile covers everyone in it, so the heading names everyone in it.
     */
    Possessive: string;
    /**
     * The possessive for ONE goal, lowercase for mid-sentence use.
     *
     * Per goal, not per household: a couple can have one person entering residential care while both
     * are on home care, and reading "Model Michael's home care support" when Tara is on a package too
     * is simply wrong (reported 2026-08-22). Lowercase because it sits inside a sentence — a single
     * capitalised form produced "Model Your aged care costs".
     */
    possessiveFor: (action: StartHereAction) => string;
    setActivePage: (page: string) => void;
    /** Where each arrangement stands; a row with nothing saved is the plain "Start" row. */
    rows?: Partial<Record<StartHereAction['goal'], StartHereRow>>;
    /** Said when two chosen plans are alternatives drawing on the same savings (aged care and support at home). */
    alternatives?: string | null;
}

/** The lead block: profile done, here is what to do about it. */
export const StartHereCard = ({ goals, completeness = null, setClientProfileStep, Possessive, possessiveFor, setActivePage, rows = {}, alternatives = null }: StartHereCardProps) => {
    const actions = [
        ...START_HERE_ACTIONS.filter((a) => goals?.[a.goal]),
        ...((rows.cashflow?.savedCount ?? 0) > 0 ? [CASHFLOW_START_HERE_ACTION] : []),
    ];
    const incomplete = !!completeness && !completeness.isComplete;
    const nextLabel = incomplete ? completeness!.steps.find((st) => !st.complete)?.label ?? null : null;
    // The card that asks for the profile first: nothing entered yet, or no goal to act on. With a goal
    // set the actions show even when later steps are unfinished — a client may model from the goals
    // step (the browser flows do) — and the unfinished profile is a line beneath them, not a wall.
    const unfinished = incomplete && (completeness!.isEmpty || actions.length === 0);

    if (unfinished) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200/70 border-l-4 border-l-blue-600 shadow-card p-6" data-testid="start-here-unfinished">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1.5">Start here</p>
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">{startHereHeading(Possessive, completeness)}</h2>
                <p className="text-sm text-slate-600 mt-1">
                    {completeness!.isEmpty
                        ? 'It takes about ten minutes, and everything after it — the options, the figures, the plan — is built from it.'
                        : `Pick up where you left off${nextLabel ? ` — ${nextLabel}` : ''}. The options and figures appear once the profile is done.`}
                </p>
                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => { setClientProfileStep?.(nextProfileStep(completeness)); setActivePage('Client Profile'); }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-blue-700"
                    >
                        {completeness!.isEmpty ? 'Start the profile' : 'Continue the profile'} <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 border-l-4 border-l-blue-600 shadow-card p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1.5">Start here</p>
            {/* Never "complete" beside "86% complete" (owner, 25 Oct 2026): an unfinished profile with goals to act on is "nearly done". */}
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">{incomplete ? `${Possessive} profile is nearly done.` : startHereHeading(Possessive, completeness)}</h2>
            <p className="text-sm text-slate-600 mt-1">
                {actions.length > 0
                    ? 'Based on the goals you set, here is what to do next.'
                    : 'No goals were set, so there is nothing to model yet. Add a goal in the profile and it will appear here.'}
            </p>
            {alternatives && <p className="text-sm text-slate-600 mt-1" data-testid="start-here-alternatives">{alternatives}</p>}
            {incomplete && (
                <p className="text-sm text-slate-600 mt-1" data-testid="start-here-incomplete-line">
                    The profile is {completeness!.percent}% complete{nextLabel ? ` — ${nextLabel} is next` : ''}.{' '}
                    <button type="button" className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800" onClick={() => { setClientProfileStep?.(nextProfileStep(completeness)); setActivePage('Client Profile'); }}>
                        Continue the profile
                    </button>
                </p>
            )}

            {actions.length > 0 && (
                <div className="mt-4 flex flex-col gap-3">
                    {actions.map((action) => {
                        const { goal, page, icon: Icon, title, blurb } = action;
                        const row = rows[goal];
                        if (row && row.savedCount > 0) {
                            const chosen = !!row.chosenTitle;
                            return (
                                <div key={goal} className="w-full flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid={`start-here-row-${goal}`}>
                                    <IconTile icon={Icon} tone="brand" variant="solid" size="md" />
                                    <span className="flex-1 min-w-[220px]">
                                        <span className="flex flex-wrap items-center gap-2">
                                            <span className="font-semibold text-slate-900">{title(possessiveFor(action))}</span>
                                            {chosen && !row.outOfDate && <span className="rounded-full border border-emerald-500 bg-white px-2 py-0.5 text-[12px] font-semibold text-emerald-800">Plan chosen</span>}
                                            {chosen && row.outOfDate && <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[12px] font-semibold text-slate-700" data-testid={`start-here-out-of-date-${goal}`}>Plan out of date</span>}
                                        </span>
                                        {/* Once something is saved, where it stands takes the blurb's place: the row stays two lines. */}
                                        <span className="block text-sm text-slate-600" data-testid={`start-here-state-${goal}`}>
                                            {chosen ? row.chosenTitle : `${row.savedCount} ${row.savedCount === 1 ? 'plan' : 'plans'} saved, none chosen yet`}
                                            {chosen && row.outOfDate ? <> · {row.outOfDate}</> : null}
                                            {row.compare && <> · <button type="button" onClick={row.compare.onClick} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">{row.compare.label}</button></>}
                                        </span>
                                    </span>
                                    <span className="shrink-0 flex flex-wrap items-center gap-2">
                                        <button type="button" onClick={row.onOpen} data-testid={`start-here-open-${goal}`} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold px-3.5 py-2 hover:bg-blue-700 transition-colors">
                                            {chosen ? `Open ${/^you( |$)/.test(possessiveFor(action)) ? 'your' : possessiveFor(action)} plan` : 'Open last saved'} <ArrowRight size={15} />
                                        </button>
                                        <button type="button" onClick={row.onStartFresh} data-testid={`start-here-start-${goal}`} title="A new model from scratch: nothing carried over, and the questions asked again" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white text-slate-800 text-sm font-semibold px-3.5 py-2 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                                            Start new
                                        </button>
                                    </span>
                                </div>
                            );
                        }
                        return (
                        <button
                            key={goal}
                            type="button"
                            onClick={() => setActivePage(page)}
                            className="group w-full text-left flex items-center gap-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 px-4 py-3.5 transition-colors"
                        >
                            <IconTile icon={Icon} tone="brand" variant="solid" size="md" />
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-slate-900">{title(possessiveFor(action))}</span>
                                <span className="block text-sm text-slate-600">{blurb}</span>
                            </span>
                            <span className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold px-3.5 py-2 group-hover:bg-blue-700 transition-colors">
                                Start <ArrowRight size={15} />
                            </span>
                        </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export interface ProfileSummaryFact {
    label: string;
    value: string;
}

interface ProfileSummaryStripProps {
    facts: ProfileSummaryFact[];
    onEdit: () => void;
}

/**
 * A one-line replacement for the Your Details and Goals Summary cards while there is nothing else
 * on the page — and, as importantly, a route BACK into the fact-find.
 *
 * That route is the second thing the same meeting turned up: the reader realised he had left his
 * father's overseas pension out, and "wasnt sure how to get back into the system to update" it.
 */
export const ProfileSummaryStrip = ({ facts, onEdit }: ProfileSummaryStripProps) => (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-card px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
        {/* A grid rather than one flex row, and no truncation: a couple with six goals across two
            categories does not fit on a line, and clipping the goals defeats the point of showing
            them (reported 2026-08-22). */}
        <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2 min-w-0 flex-1">
            {facts.map(({ label, value }) => (
                <div key={label} className="min-w-0">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-sm font-semibold text-slate-900">{value}</p>
                </div>
            ))}
        </div>
        <button
            type="button"
            onClick={onEdit}
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
        >
            <PencilLine size={15} /> Edit details
        </button>
    </div>
);

interface FoldedPlansNoteProps {
    onShowAll: () => void;
}

/** The five empty shelves, as one honest line that says what will fill them. */
export const FoldedPlansNote = ({ onShowAll }: FoldedPlansNoteProps) => (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-slate-600">
            Your saved plans will appear here once you have modelled something.
        </p>
        <button
            type="button"
            onClick={onShowAll}
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline"
        >
            Show all sections <ChevronDown size={15} />
        </button>
    </div>
);
